import React from 'react';
import { CircularProgress, MenuItem, Select } from '@mui/material';
import { MacroEditor, MacroEditorConfigProvider } from 'new/elements/MacroEditor';
// Reuse the EXACT macro-suggestion builder the legacy ConfigureAPIStage uses, so
// the URL composer produces identical macros/behavior (no rebuild).
import { getMentionsFromMacroAttrs } from 'js/components/data_sources/add_ds_form/ConfigureAPIStage';
import { extractDatasetFromApiServerFlow } from 'js/lib/MacroReplacementUtils';
import { generateSampleURL } from 'new/elements/MacroEditor/utils/macroUtils';
import { MacroBuilder } from './MacroBuilder';
import classes from './StepUrlComposer.module.scss';

/**
 * Faithful step-card header controls (prototype StepCard header):
 * Method dropdown + URL composer + Test, on one row.
 *
 * USABILITY-ONLY: the URL composer IS the existing `MacroEditor` (same config
 * provider, same suggestion list via the shared `getMentionsFromMacroAttrs`),
 * and every change writes the same config keys via `onAPIConfigChanged`:
 *   - method       (Method dropdown)
 *   - url.template  (URL composer)
 * Test calls the existing `onCallAPI(stageIndex)`. No serialization change.
 */

interface FieldOption {
  label: string;
  value: string;
}

export interface StepUrlComposerProps {
  stageIndex: number;
  /** the iteration object: apiConfig['rest.iterations'][stageIndex] */
  stageConfig: Record<string, any>;
  apiURLMacros: any;
  parentDataSet: any;
  parentDataSetSamples: any;
  editedSamples: any;
  methodOptions: FieldOption[];
  /** options for the in-popup macro settings (date.format / date.time.unit) */
  dateFormatOptions?: FieldOption[];
  timeUnitOptions?: FieldOption[];
  /** credential key→value map to resolve {data_credential["…"]} in the sample URL */
  credentialData?: Record<string, any> | null;
  isLoadingProbeSample: boolean;
  onAPIConfigChanged: (name: string, value: any, stageIndex?: number | null, data?: any) => void;
  onGetParameterizedURLs: (stageIndex: number) => any;
  onCallAPI: (stageIndex: number) => void;
}

export const StepUrlComposer: React.FC<StepUrlComposerProps> = ({
  stageIndex,
  stageConfig,
  apiURLMacros,
  parentDataSet,
  parentDataSetSamples,
  editedSamples,
  methodOptions,
  dateFormatOptions = [],
  timeUnitOptions = [],
  credentialData,
  isLoadingProbeSample,
  onAPIConfigChanged,
  onGetParameterizedURLs,
  onCallAPI,
}) => {
  // Same suggestion list the legacy form feeds the MacroEditor.
  const { macros } = getMentionsFromMacroAttrs({
    stageIndex,
    apiURLMacros,
    parentDataSet,
    parentDataSetSamples,
    editedSamples,
  });

  // The {-triggered popup IS the MacroBuilder (Time / Lookup tabs). The Time tab's
  // unit/format dropdowns write the step-level date.time.unit / date.format keys.
  const renderMacroPopup = ({
    insert,
    close,
  }: {
    insert: (raw: string) => void;
    close: () => void;
  }) => (
    <MacroBuilder
      macros={macros}
      dateFormat={stageConfig['date.format'] || 'yyyy-MM-dd'}
      dateTimeUnit={stageConfig['date.time.unit'] || 'dd'}
      dateFormatOptions={dateFormatOptions}
      timeUnitOptions={timeUnitOptions}
      onChangeSetting={(k, v) => onAPIConfigChanged(k, v, stageIndex)}
      onInsert={insert}
      onCancel={close}
    />
  );

  // Sample URLs with time macros evaluated (lookup/step combos resolved by the
  // probe; {now±N} computed here via the step's date.format / date.time.unit).
  const dateFormat = stageConfig['date.format'] || 'yyyy-MM-dd';
  const dateTimeUnit = stageConfig['date.time.unit'] || 'dd';
  const urlTemplate: string = stageConfig['url.template'] || '';
  const hasMacro = /\{[^}]+\}/.test(urlTemplate);
  const parameterized = onGetParameterizedURLs(stageIndex);
  const sampleUrls: string[] =
    parameterized?.urls?.length > 0
      ? // Lookup/step macros expand to many URLs — show at most 3 examples.
        parameterized.urls
          .slice(0, 3)
          .map((u: any) =>
            generateSampleURL(
              '',
              { urls: [u], placeholders: parameterized.placeholders ?? [] },
              undefined,
              dateFormat,
              dateTimeUnit,
              credentialData,
            ),
          )
      : hasMacro
        ? [
            generateSampleURL(
              urlTemplate,
              undefined,
              extractDatasetFromApiServerFlow(parentDataSet, parentDataSetSamples, editedSamples),
              dateFormat,
              dateTimeUnit,
              credentialData,
            ),
          ]
        : [];

  return (
    <div className={classes.composer} data-testid={`step-url-composer-${stageIndex}`}>
      <div className={classes.row}>
        <Select
          size="small"
          className={classes.method}
          value={stageConfig.method || 'GET'}
          onChange={(e) => onAPIConfigChanged('method', e.target.value, stageIndex)}
          data-testid={`step-method-${stageIndex}`}
          inputProps={{ 'aria-label': 'HTTP method' } as any}
        >
          {methodOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>

        <div className={classes.url} data-testid={`step-url-${stageIndex}`}>
          <MacroEditorConfigProvider
            dateFormat={stageConfig['date.format'] || 'yyyy-MM-dd'}
            dateTimeUnit={stageConfig['date.time.unit'] || 'dd'}
            parentDatasetSamples={extractDatasetFromApiServerFlow(
              parentDataSet,
              parentDataSetSamples,
              editedSamples,
            )}
            parameterizedURLs={onGetParameterizedURLs(stageIndex)}
          >
            <MacroEditor
              multiline
              rows={1}
              maxRows={4}
              value={stageConfig['url.template']}
              onChange={(v: string) => onAPIConfigChanged('url.template', v.trim(), stageIndex)}
              suggestions={macros.filter((m: any) => !m.empty)}
              renderMacroPopup={renderMacroPopup}
              sx={{ fontSize: '12px', lineHeight: 1.5 }}
            />
          </MacroEditorConfigProvider>
          <div className={classes.urlMeta}>
            {sampleUrls.length > 0 ? (
              <span className={classes.sampleUrlsLabel}>Sample URLs</span>
            ) : (
              <span aria-hidden="true" />
            )}
            <span className={classes.tip}>
              Hint: <code>{'{'}</code> for URL Macros
            </span>
          </div>
          {sampleUrls.length > 0 && (
            <div className={classes.sampleUrls} data-testid={`step-url-samples-${stageIndex}`}>
              {sampleUrls.map((u, i) => (
                <div key={i} className={classes.sampleUrl}>
                  {u}
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          className={classes.testButton}
          onClick={() => onCallAPI(stageIndex)}
          disabled={isLoadingProbeSample}
          data-testid={`step-test-${stageIndex}`}
        >
          {isLoadingProbeSample ? <CircularProgress size={16} color="inherit" /> : 'Test'}
        </button>
      </div>
    </div>
  );
};

export default StepUrlComposer;
